import { useMemo, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import AppShell from "@/components/feature/app/AppShell";
import PdfBrandProvider from "@/components/feature/PdfBrandProvider";
import { AgenceDataProvider } from "@/pages/agence/data/AgenceDataProvider";
import { ProprietairesProvider } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { EmployesProvider, useEmployes } from "@/pages/agence/employes/EmployesProvider";
import { RolesProvider } from "@/store/permissions/RolesProvider";
import { AbonnementProvider, useAbonnement } from "@/pages/agence/abonnement/AbonnementProvider";
import { getFormule, toEspaceFormules } from "@/pages/agence/abonnement/types";
import { useFormules } from "@/store/formules/FormulesProvider";
import { ParametresProvider, useParametres } from "@/pages/agence/parametres/ParametresProvider";
import { AGENCE_NAV_GROUPS, AGENCE_USER, filterAgenceNavGroups } from "@/config/agenceNav";
import type { AppUser } from "@/config/appNav";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import { employeNomComplet } from "@/pages/agence/employes/types";
import { initialsFromName, type PdfBrand } from "@/utils/pdfBrand";

interface AccountLink {
  label: string;
  to: string;
  icon: string;
  permission?: string;
}

const AGENCE_ACCOUNT_LINKS: AccountLink[] = [
  { label: "Profil", to: "/agence/parametres", icon: "ri-user-line" },
  { label: "Abonnement", to: "/agence/abonnement", icon: "ri-vip-diamond-line", permission: "parametres.voir" },
  { label: "Mes paiements", to: "/agence/mes-paiements", icon: "ri-history-line", permission: "parametres.voir" },
  { label: "Paramètres", to: "/agence/parametres", icon: "ri-settings-3-line", permission: "parametres.voir" },
];

function AgencePdfBrand({ children }: { children: ReactNode }) {
  const { profil } = useParametres();
  const brand = useMemo<PdfBrand>(() => {
    const name = profil.nomAgence.trim() || profil.responsable.trim() || AGENCE_USER.name;
    return {
      name,
      subtitle: "Espace Agence",
      logo: profil.logo || undefined,
      initials: initialsFromName(name),
      contact: {
        email: profil.email,
        phone: profil.telephone,
        address: profil.adresse,
        website: profil.siteWeb,
        ifu: profil.ifu,
        rccm: profil.rccm,
      },
    };
  }, [profil]);

  return <PdfBrandProvider brand={brand}>{children}</PdfBrandProvider>;
}

function AgenceShell() {
  const { can, currentEmploye, setCurrentEmployeId } = useEmployes();
  const { session } = useSession();

  const userName = session ? fullName(session) : AGENCE_USER.name;
  const user: AppUser = session
    ? { name: userName, role: session.fonction, initials: initialsFromName(userName) }
    : AGENCE_USER;

  const groups = useMemo(() => filterAgenceNavGroups(AGENCE_NAV_GROUPS, can), [can]);
  const accountLinks = useMemo(
    () => AGENCE_ACCOUNT_LINKS.filter((link) => !link.permission || can(link.permission)),
    [can]
  );
  const peutGererAbonnement = can("parametres.voir");
  const { abonnement } = useAbonnement();
  // Tarifs et limites lus dans Supabase (source de vérité) : les modifications
  // du Super Admin sont reflétées ici automatiquement.
  const { formules: catalogueFormules } = useFormules();
  const formules = useMemo(() => toEspaceFormules(catalogueFormules), [catalogueFormules]);
  const formuleAffichee = getFormule(abonnement.formuleAfficheeId, formules);

  // Un employé d'un PROPRIÉTAIRE ne doit pas accéder à l'espace Agence.
  if (session?.role === "employe_agence" && session.scope === "proprietaire") {
    return <Navigate to="/proprietaire" replace />;
  }

  const notice = currentEmploye ? (
    <div className="flex flex-col gap-2 border-b border-accent-200 bg-accent-50 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between md:px-6 lg:px-8">
      <p className="flex items-center gap-2 text-xs text-accent-900 sm:text-sm">
        <i className="ri-user-follow-line text-base" aria-hidden="true" />
        <span>
          Vous consultez l'espace avec les permissions de <strong>{employeNomComplet(currentEmploye)}</strong>. Le menu
          et les actions visibles s'adaptent à ce profil.
        </span>
      </p>
      <button
        type="button"
        onClick={() => setCurrentEmployeId(null)}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-3 py-1.5 text-xs font-label font-medium text-accent-900 transition-colors hover:bg-accent-100 cursor-pointer"
      >
        <i className="ri-arrow-go-back-line text-sm" aria-hidden="true" />
        Revenir à l'accès complet
      </button>
    </div>
  ) : undefined;

  return (
    <AppShell
      groups={groups}
      user={user}
      spaceLabel="Espace Agence"
      fallbackTitle="Tableau de bord"
      accountLinks={accountLinks}
      planLabel={peutGererAbonnement ? `Formule ${formuleAffichee.nom}` : undefined}
      planHint={
        peutGererAbonnement
          ? abonnement.paiementRequis
            ? "Paiement requis"
            : "Multi-propriétaires · Employés"
          : undefined
      }
      planTo={peutGererAbonnement ? "/agence/abonnement" : undefined}
      notice={notice}
    />
  );
}

export default function AgenceLayout() {
  return (
    <ProprietairesProvider>
      <AgenceDataProvider>
        <EmployesProvider>
          <RolesProvider>
            <AbonnementProvider>
              <ParametresProvider>
                <AgencePdfBrand>
                  <AgenceShell />
                </AgencePdfBrand>
              </ParametresProvider>
            </AbonnementProvider>
          </RolesProvider>
        </EmployesProvider>
      </AgenceDataProvider>
    </ProprietairesProvider>
  );
}